"use client";

import { ArrowRight, Menu, QrCode, X } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { Logo } from "@/components/logo";
import { ThemeToggle } from "@/components/theme-toggle";

interface CustomerNavbarProps {
  variant: "pet" | "review";
}

export function CustomerNavbar({ variant }: CustomerNavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isPet = variant === "pet";

  const config = isPet
    ? {
        href: "/pet-tag",
        suffix: "Pet",
        subtitle: "Identificação & Proteção Pet",
        buttonClass:
          "bg-orange-500 hover:bg-orange-600 text-white shadow-orange-500/20",
        demoHref: "/demo/pet",
        demoLabel: "Ver Demonstração",
      }
    : {
        href: "/google-review",
        suffix: "Review",
        subtitle: "Avaliações 5★ no Google Maps",
        buttonClass:
          "bg-[#4285F4] hover:bg-[#3367D6] text-white shadow-[#4285F4]/20",
        demoHref: "/demo/review",
        demoLabel: "Testar na Prática",
      };

  const navLinks = [
    { label: "Como Funciona", href: "#como-funciona" },
    { label: "Benefícios", href: "#beneficios" },
    { label: "No Dia a Dia", href: "#no-dia-a-dia" },
    { label: "Dúvidas Frequentes", href: "#faq" },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
        <Link
          href={config.href}
          className="flex items-center group cursor-pointer"
        >
          <Logo
            variant="subtle"
            size="lg"
            theme={variant}
            suffix={config.suffix}
            subtitle={config.subtitle}
          />
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-muted-foreground">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="transition-colors hover:text-foreground whitespace-nowrap"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3 shrink-0">
          <ThemeToggle />
          <Link
            href="/me"
            className="inline-flex items-center gap-1.5 rounded-xl border border-border px-3.5 py-2 text-xs sm:text-sm font-semibold text-foreground hover:bg-card transition-all"
          >
            <QrCode className="h-3.5 w-3.5 text-primary" />
            <span>Minhas Tags</span>
          </Link>
          <Link
            href={config.demoHref}
            className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs sm:text-sm font-semibold shadow-sm transition-all active:scale-[0.98] whitespace-nowrap ${config.buttonClass}`}
          >
            <span>{config.demoLabel}</span>
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="flex md:hidden items-center gap-2">
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="inline-flex items-center justify-center rounded-md p-2 text-muted-foreground hover:bg-card hover:text-foreground focus:outline-none"
            aria-label="Abrir menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden border-b border-border bg-background px-4 pt-2 pb-6 space-y-4 shadow-xl">
          <div className="space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="block rounded-md px-3 py-2 text-base font-medium text-muted-foreground hover:bg-card hover:text-foreground"
              >
                {link.label}
              </a>
            ))}
          </div>
          <div className="pt-3 border-t border-border flex flex-col gap-2">
            <Link
              href="/me"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold border border-border text-foreground hover:bg-card"
            >
              <QrCode className="h-4 w-4 text-primary" />
              <span>Minhas Tags (/me)</span>
            </Link>
            <Link
              href={config.demoHref}
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold shadow ${config.buttonClass}`}
            >
              <span>{config.demoLabel}</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
